import { ActionIcon, Box, Divider, Group, Text, Textarea } from '@mantine/core';
import { IconArrowUp, IconPaperclip } from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useConversations } from '../chats/useConversations';

type Props = {
  clientId: string | null;
  conversationId: string;
  onSubmit: (message: string) => void;
  onFocus?: () => void;
  submitDisabled?: boolean;
};

export function SiestaChatInput({
  clientId,
  conversationId,
  onSubmit,
  onFocus,
  submitDisabled,
}: Props) {
  const [value, setValue] = useState('');
  const { data: conversations = [] } = useConversations(clientId ?? undefined);
  const chatName =
    conversations.find(conversation => conversation.id === conversationId)?.title ?? null;

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!submitDisabled && trimmed) {
      onSubmit(trimmed);
      setValue('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box c="white">
      <Textarea
        variant="unstyled"
        size="md"
        placeholder="Ask me anything"
        value={value}
        onChange={event => setValue(event.currentTarget.value)}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        autosize
        minRows={1}
        maxRows={6}
        styles={{
          input: {
            border: 'none',
            paddingLeft: 0,
            paddingBottom: 0,
            color: 'var(--mantine-color-white)',
            '--input-placeholder-color': 'var(--mantine-color-white)',
          },
        }}
      />

      <Divider mt={2} mb="sm" color="gray.4" />

      <Group justify="space-between" align="center" wrap="nowrap">
        <ActionIcon
          variant="filled"
          color="gray"
          size="lg"
          aria-label="Attach file"
          disabled
          style={{ borderRadius: '50%' }}
        >
          <IconPaperclip size={18} />
        </ActionIcon>

        {chatName && (
          <Text flex={1} ta="center" size="sm" fw={FONT_WEIGHTS.medium} lineClamp={1}>
            {`Chat: ${chatName}`}
          </Text>
        )}

        <ActionIcon
          variant="filled"
          color="gray"
          size="lg"
          aria-label="Send message"
          onClick={handleSubmit}
          disabled={submitDisabled || !value.trim()}
          style={{ borderRadius: '50%' }}
        >
          <IconArrowUp size={18} />
        </ActionIcon>
      </Group>
    </Box>
  );
}
